/**
 * 浮标模块 —— 运行时
 * 设计文档：shopify-studio/apps/xtool/docs/float-icon-redesign-design.md
 *
 * 取代 assets/xtool.umd.js（原 apps/xtool 项目）。
 *
 * 核心不变式（改动前必读）：
 * 1. 严格读—算—写三段。读段只读 DOM，写段只写 DOM，中间不交错，避免 layout thrashing。
 * 2. 图标统一 40x40，因此位置只取决于序号，全程不读 offsetHeight（零强制 reflow）。
 * 3. 位置/显隐由 CSS 变量驱动。JS 对每个图标只做两件事：接管时写一次内联声明，
 *    序号变化时写一次 --mk-fi-i。稳态下不写 DOM。
 * 4. 全局 observer 只监听 childList + data-mk-fi-hide 属性。绝不做无过滤的 attributes 监听，
 *    否则会与自己的 style 写入形成自激循环（这是旧实现卡顿的根因）。
 *
 * 算法级回归测试：node apps/xtool/test/test.mjs（shopify-studio 仓库）
 */
(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════
   * 主题自身组件的弹窗。非商家配置，改动需要开发评估。
   * 旧的 type 1/2/3（属性 / 删除元素 / style-class）已合并为统一的可见性探测。
   *
   * ⚠️ 本表冻结在 7 条历史配置，不再新增。
   *    今后新弹窗走 data-mk-fi-hide 属性契约或 window.mkFloatIcon.acquire/release，
   *    业务方遵守规范即可，双方都不改代码。见设计文档附录 D。
   * ═══════════════════════════════════════════════════════════ */
  const THEME_DIALOGS = [
    {
      id: 'compare-drawer',
      trigger: 'a.product-variant-mobile-draw-compare-a',
      target: '#product-compare-page-mobile-drawer',
      device: 'mobile'
    },
    {
      id: 'variant-drawer',
      trigger: 'div.product-page__package-variant__image-wrapper.position-absolute',
      target: '#product-variant-mobile-drawer',
      device: 'mobile'
    },
    {
      id: 'fbt-select',
      trigger: 'mkt-select',
      target: 'ul.mkt-select__dropdown',
      targetHost: 'mkt-select',
      device: 'mobile'
    },
    {
      id: 'pc-nav-dropdown',
      trigger: '.menu-item-first-level',
      target: '.nav-dropdown-menu-container',
      scope: '.menu-item-first-level',
      on: 'hover',
      device: 'pc'
    },
    {
      id: 'old-mobile-nav',
      trigger: '.old_nav_header .tt-header-row > .tt-mobile-parent-menu',
      target: '#nav-menu-mobile-container',
      device: 'mobile'
    },
    {
      id: 'new-mobile-nav',
      trigger: '.new_nav_header .tt-mobile-parent-menu > .tt-menu-toggle',
      target: '.main-menu-mobile-v2',
      device: 'mobile'
    },
    {
      id: 'pf-halfscreen',
      trigger: '[data-open-custom-modal]',
      target: 'div.simple-product-halfscreen-modal'
    }
  ];

  const HIDE_ATTR = 'data-mk-fi-hide';
  const DECLARATIVE_ID = '__declarative';
  const PROBE_FRAMES = 30;

  let cfg = null;
  let DEBUG = false;

  const warn = (...args) => {
    if (DEBUG && window.console) console.warn('[mk-float-icon]', ...args);
  };

  /* ─── 可见性判定 ────────────────────────────────────────────
   * checkVisibility() 默认只看 display 与是否被渲染，不看 opacity / visibility ——
   * 这正是我们需要的，因为 opacity/visibility 是本模块自己在用的状态通道。
   */
  const isVisible = (el) => {
    if (!el?.isConnected) return false;
    if (typeof el.checkVisibility === 'function') return el.checkVisibility();
    const cs = window.getComputedStyle(el);
    if (cs.display === 'none') return false;
    return el.offsetWidth > 0 || el.offsetHeight > 0 || cs.position === 'fixed';
  };

  /* ─── 弹窗「是否打开」判定 ──────────────────────────────────
   * 与 isVisible 的差别是本质性的，不能混用：
   *
   *   图标：opacity / visibility 是【本模块自己的通道】（--mk-fi-o / --mk-fi-v），
   *         必须忽略，否则闸门期间的打底会让我们永远探测不到图标。
   *   弹窗：我们从不写入弹窗目标，opacity / visibility 恰恰是【第三方自己的开关信号】，
   *         必须检查。
   *
   * 反例（实测）：关闭状态的 <mkt-drawer id="product-variant-mobile-drawer"> 是
   * display:flex + 满屏 390x844 + visibility:hidden + opacity:0，
   * 默认的 checkVisibility() 返回 true —— 用 isVisible 判它会误认为弹窗一直开着，
   * 导致商品页上的图标从启动起就被永久隐藏。
   *
   * 这也让旧实现的三种检测类型真正统一：
   *   type 1 属性型   → 属性驱动 CSS → visibility / opacity 变化，这里能测到
   *   type 2 删除元素 → isConnected 为 false
   *   type 3 style/class → display / opacity 变化
   */
  const DIALOG_VIS_OPTS = {
    opacityProperty: true,
    visibilityProperty: true,
    contentVisibilityAuto: true,
    // 旧版命名别名，兼容尚未更名的引擎
    checkOpacity: true,
    checkVisibilityCSS: true
  };

  const isDialogOpen = (el) => {
    if (!el?.isConnected) return false;
    if (typeof el.checkVisibility === 'function') return el.checkVisibility(DIALOG_VIS_OPTS);
    const cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return false;
    return el.offsetWidth > 0 || el.offsetHeight > 0;
  };

  /*
   * 粗筛：容器的存在性变化只可能源于 Element 节点的增删。
   * 文字节点变更（输入框打字、aria-live 更新）非常高频，直接放过可显著降负载。
   * attributes 类型必须放行 —— filter 已把范围收窄到 data-mk-fi-hide 一个属性。
   */
  const isRelevantMutation = (records) => {
    for (const m of records) {
      if (m.type === 'attributes') return true;
      for (const n of m.addedNodes) if (n.nodeType === 1) return true;
      for (const n of m.removedNodes) if (n.nodeType === 1) return true;
    }
    return false;
  };

  /* ═══ createResolver ══════════════════════════════════════
   * 选择器 → 元素。唯一懂 Shadow DOM 的地方。
   */
  const createResolver = (onSignal) => {
    const bad = new Set();
    const shadowObservers = new Map();
    const definedTags = new Set();
    const revealed = new WeakSet();

    const q = (root, sel) => {
      try {
        return root.querySelector(sel) || null;
      } catch {
        if (!bad.has(sel)) warn('非法选择器，已永久跳过：', sel);
        bad.add(sel);
        return null;
      }
    };

    const ensureUpgrade = (host) => {
      const tag = host.localName;
      if (!tag || !tag.includes('-') || definedTags.has(tag)) return;
      if (typeof window.customElements?.whenDefined !== 'function') return;
      definedTags.add(tag);
      window.customElements.whenDefined(tag).then(onSignal).catch(() => {});
    };

    const ensureShadowObserver = (host) => {
      if (!host.shadowRoot || shadowObservers.has(host)) return;
      const ob = new MutationObserver((records) => {
        if (isRelevantMutation(records)) onSignal();
      });
      ob.observe(host.shadowRoot, { childList: true, subtree: true });
      shadowObservers.set(host, ob);
    };

    /*
     * 宿主显形（旧 initHiddenElement 的等价物）。
     * 外部样式表选不中 shadowRoot 内的图标，防闪现只能隐藏宿主本身：目前由
     * snippets/icon-css.liquid 的历史规则承担（body > mkt-wheel { opacity: 0 }）——
     * ⚠️ 阶段 3 清理 icon-css 时不能连它一起删，见该文件内的标注。
     * 注意不能把宿主隐藏聚合进 section 打底：同名宿主可能有多实例（body > pt-experience），
     * 而 reveal 只显形图标所在的那一个，聚合会把其余实例里的第三方弹窗一并藏掉。
     * 只在图标真的找到之后才显形，避免宿主先露出一个未定位的图标。
     * 这是读段里唯一的一次写入，每个宿主仅一次，故不构成 thrashing。
     */
    const reveal = (host) => {
      if (revealed.has(host)) return;
      revealed.add(host);
      host.style.setProperty('opacity', '1', 'important');
    };

    return {
      resolve(sel, hostSel) {
        if (!sel || bad.has(sel)) return null;
        if (!hostSel) return q(document, sel);
        if (bad.has(hostSel)) return null;
        const host = q(document, hostSel);
        if (!host) return null;
        ensureUpgrade(host);
        ensureShadowObserver(host);
        if (!host.shadowRoot) return null;
        const el = q(host.shadowRoot, sel);
        if (el) reveal(host);
        return el;
      },

      isBad: (sel) => !!sel && bad.has(sel),

      cleanup() {
        for (const [host, ob] of shadowObservers) {
          if (!host.isConnected) {
            ob.disconnect();
            shadowObservers.delete(host);
          }
        }
      }
    };
  };

  /* ═══ createRegistry ══════════════════════════════════════
   * 配置 → 条目数组。启动时一次性构建，之后只读。
   * icons 按 block 顺序，index 0 = 视觉最上方。
   */
  const createRegistry = (json) => ({
    icons: (json?.icons ?? [])
      .filter((c) => c?.probeSel)
      .map((c) => ({
        key: c.key || '',
        kind: c.kind === 'owned' ? 'owned' : 'foreign',
        probeSel: c.probeSel,
        probeHost: c.probeHost || '',
        popupSel: c.popupSel || '',
        popupHost: c.popupHost || c.probeHost || '',
        track: c.track || '',
        themeVis: !!c.themeVis,
        node: null,
        hit: false,
        slot: -1,
        nextSlot: -1,
        adopted: false,
        broken: false
      }))
  });

  /* ═══ createStyleEnforcer ═════════════════════════════════
   * 样式接管。唯一写图标 style 属性的地方。
   *
   * 内联 !important 是为了打赢第三方的内联 !important；
   * 但值指向 CSS 变量，所以真正的数值仍由样式表决定。
   *
   * ⚠️ INLINE 必须与 mk-float-icon.css 的「隐藏组」逐属性对齐，
   *    否则打底会在接管后永久残留。
   */
  /*
   * 位置公式必须内联在元素的 bottom 上，不能走 :root 上的中间变量 ——
   * 自定义属性里的 var() 在声明处就被替换并固化，详见 mk-float-icon.css 的说明。
   */
  const BOTTOM_CALC =
    'calc(var(--mk-fi-base) + var(--mk-fi-i, 0) * (var(--mk-fi-size) + var(--mk-fi-gap)))';

  // 位置与遮挡通道：所有图标都写
  const INLINE_BASE = [
    ['bottom', BOTTOM_CALC],
    ['right', 'var(--mk-fi-r)'],
    ['z-index', 'var(--mk-fi-z)'],
    ['pointer-events', 'var(--mk-fi-pe)'],
    ['clip-path', 'var(--mk-fi-cp)']
  ];

  /*
   * 显隐通道：只对「显隐由本模块接管」的图标写。
   *
   * 有些图标自己就有显隐规则，典型是回到顶部按钮 —— 主题的 section.footer.js 按滚动阈值
   * 给它加 .show class，CSS 用 opacity/visibility 控制。我们若用内联 !important 写这两个属性，
   * 就把阈值控制整个覆盖掉了，结果是首屏就显示（实测 bug）。
   * 这类图标勾选 theme_controls_visibility 后，opacity / visibility / transition 全部还给主题，
   * 我们只用 clip-path + pointer-events 做滚动/遮挡隐藏 —— 这两个属性没有第三方在用。
   *
   * transition 归入本组是因为：我们的 opacity 切换会触发元素自带的过渡动画
   * （实测回到顶部按钮 0.5s、owned 图标 transition:all），导致显隐肉眼可见地慢。
   * 由本模块接管显隐时统一置 none 求即时；还给主题时保留主题自己的过渡。
   */
  const INLINE_VIS = [
    ['opacity', 'var(--mk-fi-o)'],
    ['visibility', 'var(--mk-fi-v)'],
    ['transition', 'var(--mk-fi-tr)']
  ];

  const inlineFor = (e) => (e.themeVis ? INLINE_BASE : INLINE_BASE.concat(INLINE_VIS));

  /*
   * 布局重置。CSS 打底（不带 important）管多数派；
   * 这里只在第三方确实写了内联声明时逐属性反制 —— 内联非 important 也会打赢样式表，
   * 所以判据是「有没有内联声明」，不是「有没有 important」。
   */
  const SIZE_RESET = [
    ['width', 'var(--mk-fi-size)'],
    ['height', 'var(--mk-fi-size)'],
    ['min-width', '0'],
    ['min-height', '0'],
    ['margin', '0'],
    ['transform', 'none'],
    ['top', 'auto'],
    ['left', 'auto']
  ];

  const createStyleEnforcer = () => {
    const selfWatched = new WeakSet();

    /*
     * 给已接管的图标挂一个只看 style / class 的窄 observer。
     *
     * 必需性（实测）：点击客服图标打开面板时，其 SDK 会把
     *   width/height: var(--mk-fi-size)!important → 42px
     *   z-index: var(--mk-fi-z)!important        → 800
     *   class: "mk-popover-open mk-fi-managed mk-fi--mk-bot-icon" → ""
     * 全部覆盖掉。而全局 observer 只监听 childList，所以我们要等下一次 DOM 结构变化
     * 才恢复 —— 实测延迟 2.8 秒，用户看到的就是「图标闪一下、过会儿又变回来」。
     *
     * 不会自激：回调只调 ensure()，而 ensure 逐属性比对、值一致就不写。
     * 我们写 → observer 触发 → 比对通过 → 不写 → 收敛。
     */
    const watchSelf = (e) => {
      const el = e.node;
      if (selfWatched.has(el)) return;
      selfWatched.add(el);
      const ob = new MutationObserver(() => api.ensure(e));
      ob.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });
    };

    const adopt = (e) => {
      const el = e.node;
      el.classList.add('mk-fi-managed');
      if (e.key) el.classList.add(`mk-fi--${e.key}`);
      if (e.track) {
        el.classList.add('track-ele');
        el.setAttribute('data-action', 'click-float-elements');
        el.setAttribute('data-action-attr__content', e.track);
      }
      for (const [prop, val] of inlineFor(e)) el.style.setProperty(prop, val, 'important');
      e.adopted = true;
      watchSelf(e);
    };

    // 每轮廉价校正：防第三方在我们接管之后异步回写内联样式
    const counterInline = (el) => {
      for (const [prop, val] of SIZE_RESET) {
        const cur = el.style.getPropertyValue(prop);
        if (cur !== '' && cur !== val) el.style.setProperty(prop, val, 'important');
      }
    };

    const api = {
      /*
       * 幂等校验必须覆盖全部通道，不能只比对 bottom。
       * 第三方若在我们接管之后写 el.style.opacity = 0，CSSOM 会直接替换掉我们那条
       * opacity: var(--mk-fi-o) !important 声明；而 isVisible 依赖 checkVisibility()、
       * 默认不看 opacity，于是图标变透明、我们毫不知情，还继续给它占着槽位 ——
       * 结果是一个看不见的图标占着位置。逐通道比对的成本是每轮几次字符串比较。
       *
       * class 必须单独校验：第三方可能只清空 className 而不动内联样式（客服 SDK 实测
       * 会清 class），此时逐属性比对全部通过，但 mk-fi-managed / track-ele 已丢失 ——
       * 埋点从此漏报、per-icon CSS 逃生舱失效。
       */
      ensure(e) {
        const el = e.node;
        if (!el) return;
        if (!el.classList.contains('mk-fi-managed')) {
          adopt(e);
        } else {
          for (const [prop, val] of inlineFor(e)) {
            if (el.style.getPropertyValue(prop) !== val) {
              adopt(e);
              break;
            }
          }
        }
        /*
         * --mk-fi-i 的唯一写入点。它不在 adopt 的清单里（那些值是常量，而序号是变量），
         * 由 sync 先把 e.slot 定好、这里逐值比对写入。
         * 必须在这里而不是 sync 里按「槽位变化」写：第三方整体重写 style 属性后，
         * 若槽位恰好没变就永远没人补回，bottom 公式回退 var(--mk-fi-i, 0)=0，
         * 图标跌到最底部槽位与别人重叠（T9 覆盖）。
         */
        if (e.slot >= 0 && el.style.getPropertyValue('--mk-fi-i') !== String(e.slot)) {
          el.style.setProperty('--mk-fi-i', String(e.slot));
        }
        counterInline(el);
      }
    };

    return api;
  };

  /* ═══ createOverlay ═══════════════════════════════════════
   * 遮挡状态，引用计数。三条来源共用：
   *   ① THEME_DIALOGS 探测  ② data-mk-fi-hide 属性  ③ window.mkFloatIcon.acquire
   */
  const createOverlay = (resolver) => {
    const active = new Map();   // id → { target, observer } | null

    const apply = () => document.body.classList.toggle('mk-fi-overlay', active.size > 0);

    const acquire = (id, rec = null) => {
      if (active.has(id)) return;
      active.set(id, rec);
      apply();
    };

    const dropRec = (rec) => {
      rec?.observer?.disconnect();
      if (rec?.timer) clearTimeout(rec.timer);
    };

    const release = (id) => {
      if (!active.has(id)) return;
      dropRec(active.get(id));
      active.delete(id);
      apply();
    };

    // 给某个已确认打开的弹窗元素挂局部 observer，关闭即回收，回到零监听
    const watch = (id, el) => {
      const cur = active.get(id);
      if (cur) {
        if (cur.target === el) return;
        // 换目标（如导航从菜单项 A 移到 B）：断开旧 observer 重挂，不能直接返回
        dropRec(cur);
        active.delete(id);
      }
      const rec = { target: el, observer: null, timer: null };
      /*
       * 过渡关闭的复查。弹窗用 class 切换 + CSS opacity 过渡关闭时，class 移除的瞬间
       * computed opacity 仍≈1（observer 回调判定「还开着」），而过渡结束后不再产生任何
       * mutation —— 没有这个复查，遮挡会永久卡住、图标持续隐藏。
       *
       * 必须是有限次轮询而非单次：复查点落在过渡中途时 opacity 可能是 0.3，
       * 而 opacityProperty 只把恰好 0 视作不可见，单次复查会判「仍打开」后就再无人接手。
       * 3 × 600ms 覆盖到 1.8s 的过渡；耗尽后停手，长时间打开的弹窗由此不受影响
       * （真正的关闭若发生在更晚，会伴随新的 mutation 重新触发本机制）。
       */
      const REVALIDATE_TRIES = 3;
      const scheduleRevalidate = (left) => {
        if (rec.timer) clearTimeout(rec.timer);
        if (left <= 0) {
          rec.timer = null;
          return;
        }
        rec.timer = setTimeout(() => {
          rec.timer = null;
          if (!isDialogOpen(rec.target)) release(id);
          else scheduleRevalidate(left - 1);
        }, 600);
      };
      const ob = new MutationObserver(() => {
        if (!isDialogOpen(rec.target)) {
          release(id);
          return;
        }
        scheduleRevalidate(REVALIDATE_TRIES);
      });
      /*
       * 目标自身的属性变化 + 父节点的子列表变化（被整体删除）。
       * 属性【不加 filter】：关闭信号可能是任意属性 —— mkt-drawer 靠移除 visible 属性关闭，
       * 只监听 style/class 会永远收不到关闭通知。范围仅这一个元素、且只在弹窗打开期间，成本可忽略。
       * 父节点不限于 Element：弹窗可能是 shadowRoot（DocumentFragment）的直接子节点，
       * MutationObserver 观察 Document / DocumentFragment 均合法，收窄到 nodeType 1 会漏掉
       * 「shadow 内弹窗以删除元素方式关闭」这条路径。
       */
      ob.observe(el, { attributes: true });
      if (el.parentNode) ob.observe(el.parentNode, { childList: true });
      rec.observer = ob;
      acquire(id, rec);
    };

    const countDeclarative = () => {
      let n = 0;
      for (const el of document.querySelectorAll(`[${HIDE_ATTR}]`)) {
        // 业务方的元素我们从不写入，所以用弹窗判据（opacity / visibility 算隐藏）
        if (isDialogOpen(el)) n++;
      }
      return n;
    };

    const setDeclarative = (n) => (n > 0 ? acquire(DECLARATIVE_ID) : release(DECLARATIVE_ID));

    return {
      acquire,
      release,
      watch,
      countDeclarative,
      setDeclarative,
      isActive: () => active.size > 0,
      has: (id) => active.has(id),
      sources: () => [...active.keys()],

      /*
       * 兜底：漏网场景是「祖先被隐藏，target 自身无 mutation」。
       * sync 的读段本来就在跑，顺手校验（active 通常 0 或 1 个）。
       * 不用定时器强制恢复 —— 那会误伤长时间打开的弹窗。
       */
      revalidate() {
        for (const [id, rec] of active) {
          if (rec?.target && !isDialogOpen(rec.target)) release(id);
        }
      },

      /*
       * 闸门打开时的开场盘点。不依赖任何 trigger ——
       * 用户可能在闸门期间就点开了弹窗，那次点击我们没监听到。
       * 必须先于首次 sync 调用，这样 body.mk-fi-overlay 在图标接管之前就已加上，
       * --mk-fi-o 解析为 0，图标接管的那一刻就是透明的，一帧都不会闪。
       */
      scanOnce(dialogs) {
        for (const d of dialogs) {
          const el = resolver.resolve(d.target, d.targetHost);
          if (el && isDialogOpen(el)) watch(d.id, el);
        }
      },

      /* 声明式遮挡契约。见设计文档 §5.5 / 附录 D。 */
      syncDeclarative() {
        setDeclarative(countDeclarative());
      },

      /* 闸门期间业务方调过的 acquire，启动后补上 */
      replay(ids) {
        for (const id of ids) acquire(id);
      }
    };
  };

  /* ═══ createPopupAnchor ═══════════════════════════════════
   * 弹窗右下角 ↔ 图标左下角，底边齐平。
   * 与图标共用同一个位置公式和同一个序号，因此不需要读取图标的实际像素值
   * （旧实现的 iconBottomMap 因此消失）。
   * 变量在 :root 定义、穿透 shadow 继承，所以 Shadow DOM 内的弹窗同样生效。
   */
  const createPopupAnchor = (resolver) => {
    const RIGHT = 'calc(var(--mk-fi-r) + var(--mk-fi-size) + var(--mk-fi-popup-gap))';
    return {
      /*
       * 每轮 sync 都调用，因为弹窗元素可能晚于图标出现（甚至被反复重建）。
       * 值未变则直接返回，所以稳态下不写 DOM。
       */
      sync(e, slot) {
        const popup = resolver.resolve(e.popupSel, e.popupHost);
        if (!popup) return;
        const bottom = `calc(var(--mk-fi-base) + ${slot} * (var(--mk-fi-size) + var(--mk-fi-gap)))`;
        // 两个属性都要参与幂等比对：第三方可能只覆盖 right 而不动 bottom，
        // 只看 bottom 会判定「已同步」直接返回，水平对齐从此永久错位。
        if (
          popup.style.getPropertyValue('bottom') === bottom &&
          popup.style.getPropertyValue('right') === RIGHT
        ) return;
        popup.style.setProperty('bottom', bottom, 'important');
        popup.style.setProperty('right', RIGHT, 'important');
      }
    };
  };

  /* ═══ createDialogWatcher ═════════════════════════════════
   * body 上仅 2 个委托监听，都走 capture 阶段。
   * 用 composedPath() 匹配 trigger —— 一次性覆盖普通 DOM 与 Shadow DOM 内的 trigger，
   * 旧实现为此单独写了 handleWebComponent 去 shadowRoot 里绑事件。
   */
  const createDialogWatcher = (dialogs, resolver, overlay) => {
    const deviceOk = (d) => {
      if (!d.device) return true;
      // 与 mk-float-icon.css 的断点（max-width: 1024px）取反保持同一边界，
      // 避免非整数视口宽度（如 1024.5px）落进两套判定之间的缝隙
      const isPc = !window.matchMedia('(max-width: 1024px)').matches;
      return d.device === 'pc' ? isPc : !isPc;
    };

    const matchTrigger = (d, path) => {
      for (const n of path) {
        if (n?.nodeType !== 1 || !n.matches) continue;
        try {
          if (n.matches(d.trigger)) return n;
        } catch {
          return null;
        }
      }
      return null;
    };

    const findTarget = (d, hitEl, path) => {
      // scope：同一 trigger 有多个实例时，从命中元素向上找容器再向下找弹窗
      const scopeEl = d.scope ? hitEl?.closest?.(d.scope) : null;
      if (scopeEl) {
        try {
          return scopeEl.querySelector(d.target);
        } catch {
          return null;
        }
      }
      /*
       * targetHost 必须在【事件路径上】解析，不能用 document.querySelector ——
       * 同名 Web Component 常有多个实例。实测商品页有 4 个 mkt-select（规格 tab），
       * 全局取第一个会落到与本次点击无关的实例上、找不到弹窗，探测直接超时。
       * 旧实现用 event.target.shadowRoot 保住了「在被点击的那个实例里找」这个语义，
       * 这里用 composedPath 等价实现，且兼容「trigger 就是宿主」与「trigger 在宿主内部」两种形态。
       */
      if (d.targetHost && path) {
        for (const n of path) {
          if (n?.nodeType !== 1 || !n.matches) continue;
          let hit = false;
          try {
            hit = n.matches(d.targetHost);
          } catch {
            break;   // targetHost 选择器非法
          }
          if (!hit) continue;
          if (!n.shadowRoot) return null;
          try {
            return n.shadowRoot.querySelector(d.target) || null;
          } catch {
            return null;
          }
        }
      }
      // 兜底：scanOnce 没有事件路径可用，只能走全局解析
      return resolver.resolve(d.target, d.targetHost);
    };

    /*
     * rAF 多帧轮询。部分 dropdown 展开有 hoverIntent / CSS transition 延迟
     * （实测 PC 导航约 300ms），固定延时会误判为「未显示」。最多等 ~500ms；
     * 等不到就什么都不留下。
     *
     * 用世代号让新 hover【取代】在飞的探测，而不是被它挡住 —— 这一点是必需的：
     * 曾用一个 _probing 布尔锁做去重，结果 hover 菜单项 A（其 dropdown 因 hoverIntent
     * 还没开、模块正处在 500ms 探测窗口内）时移到 B，B 的 hover 被锁整个吞掉；
     * 而鼠标停在 B 上不再产生 mouseover，于是【永远不会恢复】，图标一直压在
     * B 的 dropdown 上。世代号方案下旧循环自行放弃，新的立刻接管。
     */
    const probe = (d, hitEl, path) => {
      const gen = (d._gen = (d._gen || 0) + 1);
      let frames = 0;
      const check = () => {
        if (d._gen !== gen) return;   // 已被更新的 hover 取代
        const el = findTarget(d, hitEl, path);
        if (el && isDialogOpen(el)) {
          overlay.watch(d.id, el);
          return;
        }
        if (++frames < PROBE_FRAMES) requestAnimationFrame(check);
      };
      requestAnimationFrame(check);
    };

    const dispatch = (ev, kind) => {
      const path = ev.composedPath?.() ?? [ev.target];
      for (const d of dialogs) {
        if ((d.on === 'hover' ? 'hover' : 'click') !== kind) continue;
        if (!deviceOk(d)) continue;
        const hit = matchTrigger(d, path);
        if (!hit) continue;
        /*
         * 去重条件必须同时看「探测中」和「已遮挡」两个状态。
         * 只记「上次命中的元素」是不够的：hover 菜单项 A → 离开（dropdown 关闭、遮挡解除）
         * → 再次 hover A，会被误判为重复而跳过，图标就会压在 dropdown 上。
         */
        /*
         * 去重必须看「命中的是不是同一个元素」，不能只看「该 dialog 是否已遮挡」。
         * 反例（实测）：PC 导航从菜单项 A 横向移到 B —— A 的 dropdown 关闭、B 的打开。
         * 若因为 overlay.has('pc-nav-dropdown') 为真而跳过 B，我们的局部 observer 仍盯着
         * A 的元素；A 一关就 release，而 B 明明开着，图标就压在 B 的 dropdown 上。
         * 按命中元素去重后，A→B 会重新探测并把 observer 换到 B 上。
         */
        if (d._lastHit === hit && overlay.has(d.id)) continue;
        d._lastHit = hit;
        probe(d, hit, path);
      }
    };

    return {
      attach() {
        document.addEventListener('click', (ev) => dispatch(ev, 'click'), true);
        // mouseenter 不冒泡，必须用 mouseover 委托
        document.addEventListener('mouseover', (ev) => dispatch(ev, 'hover'), true);
      }
    };
  };

  /* ═══ createScrollGate ════════════════════════════════════
   * 滚动隐藏 = 一次 body classList 操作。
   * 替掉旧实现的 userTouchFlag / scrollFlag / startX / startY / touchMoveTimer
   * 五个状态、位移比值判断、touchend 150ms 补丁与 isRealDevice()。
   */
  const createScrollGate = (delay) => {
    let away = false;
    let timer = null;
    let lastY = window.scrollY;

    const onScrollish = () => {
      if (window.scrollY === lastY) return;   // 横滑 / 页面高度变化引起的伪滚动
      lastY = window.scrollY;
      if (!away) {
        away = true;
        document.body.classList.add('mk-fi-away');
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        away = false;
        document.body.classList.remove('mk-fi-away');
      }, delay);
    };

    return {
      attach() {
        window.addEventListener('scroll', onScrollish, { passive: true });
        // iOS 快滑时 scroll 可能滞后，touchmove 作为补充信号，走同一个守卫
        window.addEventListener('touchmove', onScrollish, { passive: true });
      }
    };
  };

  /* ═══ 调度 ════════════════════════════════════════════════ */

  let registry;
  let resolver;
  let enforcer;
  let overlay;
  let popupAnchor;
  let scheduled = false;

  const scheduleSync = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      sync();
    });
  };

  function sync() {
    const { icons } = registry;

    /* ── 读：只读 DOM ── */
    for (const e of icons) {
      if (e.broken) continue;
      if (resolver.isBad(e.probeSel) || resolver.isBad(e.probeHost)) {
        e.broken = true;
        e.hit = false;
        continue;
      }
      if (e.kind === 'foreign') {
        e.node = resolver.resolve(e.probeSel, e.probeHost);
        e.hit = !!e.node && isVisible(e.node);
      } else {
        // owned：探测目标只决定显隐，被定位的始终是我们渲染的 button
        e.hit = !!resolver.resolve(e.probeSel, e.probeHost);
      }
      /*
       * 空间预留（旧实现 popupOpenItems 的等价物）。
       * 有些第三方在图标被点击后直接把图标元素删掉、换成弹窗（pt-experience 就是这样）。
       * 此时若不为它保留槽位，它上方的图标会在弹窗打开期间整体下移一格。
       * 判据就是「它的关联弹窗当前可见」，因此只对配了 popup_selector 的条目生效 ——
       * 与旧实现只对配了 popupElement 的条目生效完全一致。
       *
       * e.adopted 前置条件必不可少：它把语义收窄为「图标曾出现过、现在消失了」。
       * 否则「本页图标恒不存在、而 popup_selector 命中了页面上的常驻元素」也会预留 ——
       * 实测桌面端博客文章页：#blog-icon 恒 display:none，而 .blog-sidebar 是常驻左侧
       * 目录（不是弹窗），没有这个条件时图标竖列会永久多出一个 52px 的空洞。
       * 代价：弹窗在图标被接管之前就已打开（如刷新后 ptengine 直接弹出）时不预留，
       * 该轮弹窗不做跟随定位 —— 可接受，图标列本身无空洞。
       */
      if (!e.hit && e.popupSel && e.adopted) {
        const pop = resolver.resolve(e.popupSel, e.popupHost);
        // 关联弹窗同理用弹窗判据 —— 否则一个 opacity:0 的关闭弹窗会白占一个槽位
        if (pop && isDialogOpen(pop)) e.hit = true;
      }
    }
    overlay.revalidate();
    resolver.cleanup();
    const declCount = overlay.countDeclarative();

    /* ── 算：不碰 DOM ──
     * block 顺序是「从上到下」，而 --mk-fi-i 是「从下往上计数」，方向相反 → 倒序遍历
     */
    let slot = 0;
    for (let i = icons.length - 1; i >= 0; i--) {
      const e = icons[i];
      e.nextSlot = e.hit ? slot++ : -1;   // 注意不看 e.node —— 预留槽位时它可能已被删除
    }

    /* ── 写：只写 DOM ── */
    overlay.setDeclarative(declCount);
    const taken = new Set();
    for (const e of icons) {
      if (e.kind === 'owned' && e.node) {
        e.node.classList.toggle('mk-fi-on', e.nextSlot !== -1);
      }
      if (e.nextSlot === -1) {
        e.slot = -1;
        continue;
      }
      if (!e.node) {
        // 槽位是为「图标已被删除但弹窗还开着」预留的：只把弹窗定到该槽位，不写图标
        e.slot = e.nextSlot;
        if (e.popupSel) popupAnchor.sync(e, e.nextSlot);
        continue;
      }
      if (taken.has(e.node)) {
        warn('两个配置指向同一元素，后者已跳过：', e.key);
        e.slot = -1;
        continue;
      }
      taken.add(e.node);
      // 先定槽位，再由 ensure 统一写入 —— --mk-fi-i 只有 ensure 一个写入点，
      // 它逐值比对：槽位变了就写，没变但被第三方清掉了也补回，值正确则不写。
      // （拆成两处写会让「槽位未变时补回丢失的变量」这条路径漏掉，正是 bug 的来源。）
      e.slot = e.nextSlot;
      enforcer.ensure(e);
      // 不放进条件里：弹窗元素可能晚于图标出现，那时序号已经不再变化了。
      // popupAnchor.sync 自身幂等，值未变直接返回，所以稳态下不写 DOM。
      if (e.popupSel) popupAnchor.sync(e, e.nextSlot);
    }
  }

  const attachSignals = () => {
    const ob = new MutationObserver((records) => {
      if (isRelevantMutation(records)) scheduleSync();
    });
    ob.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [HIDE_ATTR]
    });
  };

  /* ═══ owned 图标点击 ══════════════════════════════════════ */
  const attachOwnedClicks = () => {
    for (const btn of document.querySelectorAll('.mk-fi-owned[data-popup-id]')) {
      btn.addEventListener(
        'click',
        (ev) => {
          const id = ev.currentTarget.getAttribute('data-popup-id');
          if (!id) return;
          if (typeof window.ptengine?.showEngagement === 'function') {
            window.ptengine.showEngagement(id);
          } else {
            warn('ptengine 未就绪，showEngagement 未执行：', id);
          }
        },
        true
      );
    }
  };

  /* ═══ 启动闸门 ════════════════════════════════════════════
   * 浮标是页面上优先级最低的东西，让内容先渲染完。
   * 闸门打开前零 JS 活动：不解析 registry、不挂 observer、不查 DOM、不绑弹窗监听。
   * 闸门期间图标由打底 CSS 隐藏，所以漏掉的弹窗遮挡没有视觉后果。
   */
  let booted = false;
  const pendingAcquire = new Set();   // 闸门期间业务方调过的 acquire，启动后补上

  function boot() {
    if (booted) return;
    booted = true;

    resolver = createResolver(scheduleSync);
    registry = createRegistry(cfg);
    enforcer = createStyleEnforcer();
    overlay = createOverlay(resolver);
    popupAnchor = createPopupAnchor(resolver);

    // owned 图标的 node 在启动时一次性缓存
    for (const e of registry.icons) {
      if (e.kind !== 'owned') continue;
      e.node = e.key ? document.querySelector(`.mk-fi-owned.mk-fi--${e.key}`) : null;
      if (!e.node) {
        e.broken = true;
        warn('owned 图标 DOM 未找到：', e.key);
      }
    }

    // ⚠️ 顺序 load-bearing：遮挡态必须在首次 sync 之前确立，否则图标会闪一帧
    overlay.scanOnce(THEME_DIALOGS);
    overlay.syncDeclarative();
    overlay.replay(pendingAcquire);
    pendingAcquire.clear();

    sync();

    attachSignals();
    attachOwnedClicks();
    createDialogWatcher(THEME_DIALOGS, resolver, overlay).attach();
    if (cfg.scrollHide !== false) {
      // ?? 而非 ||：配 0 是合法值（停止滚动立即恢复），与 bootDelay 的口径一致
      createScrollGate(cfg.scrollRestoreDelay ?? 500).attach();
    }

    warn('已启动，图标数：', registry.icons.length);
  }

  /* ═══ 对外接口 ════════════════════════════════════════════ */
  const exposeApi = () => {
    window.mkFloatIcon = {
      acquire(id) {
        if (!id) return;
        // 闸门未开时先记下来，boot 时回放 —— 否则业务方以为调用成功了，其实被丢弃
        if (!overlay) {
          pendingAcquire.add(String(id));
          return;
        }
        overlay.acquire(String(id));
      },

      release(id) {
        if (!id) return;
        if (!overlay) {
          pendingAcquire.delete(String(id));
          return;
        }
        overlay.release(String(id));
      },

      dump() {
        if (!registry) return '[mk-float-icon] 尚未启动';
        const rows = registry.icons.map((e, i) => ({
          block: i + 1,
          key: e.key,
          kind: e.kind,
          hit: e.hit,
          slot: e.slot,
          broken: e.broken,
          node: e.node
        }));
        window.console?.table?.(rows);
        return { icons: rows, overlaySources: overlay.sources(), config: cfg };
      }
    };
  };

  /* ═══ 入口 ════════════════════════════════════════════════ */
  const holder = document.getElementById('mk-fi-config');
  if (!holder) return;
  try {
    cfg = JSON.parse(holder.textContent);
  } catch (err) {
    // 配置解析失败则完全不启动：图标退化到第三方原生位置，页面完全可用，绝不抛异常
    window.console?.error('[mk-float-icon] 配置解析失败，模块未启动', err);
    return;
  }
  if (!cfg || cfg.enabled === false || !cfg.icons?.length) return;
  DEBUG = !!cfg.debug;

  exposeApi();

  const delay = cfg.bootDelay ?? 3000;
  const hardCap = cfg.bootHardCap ?? 6000;

  if (document.readyState === 'complete') {
    setTimeout(boot, delay);
  } else {
    window.addEventListener('load', () => setTimeout(boot, delay), { once: true });
  }
  // 兜底：load 因某个挂住的资源永不触发时，仍要保证浮标出现
  setTimeout(boot, hardCap);
})();
